import { useEffect, useState } from 'react';
import { usePointerClass, type PointerClass } from '../layout/sizeClass';

// ============================================================================
// useHardwareKeyboard (E0/Paket A · Q1 — Kontrakt aus docs/30 §9.9)
// ----------------------------------------------------------------------------
// Beantwortet die einzige Frage, an der die „Enter sendet"-Logik haengen darf:
// Ist GERADE eine echte Hardware-Tastatur im Einsatz?
//
// Warum nicht (any-pointer: fine)? Weil ein gekoppelter Apple Pencil oder eine
// Maus ohne Tastatur `any-pointer: fine` bereits erfuellt — dann wuerde die
// Return-Taste der BILDSCHIRM-Tastatur absenden statt umzubrechen (Regression
// fuer mehrzeilige Rollenspiel-Beitraege).
//
// Kontrakt (docs/30 §9.9): Hardware gilt als beobachtet nach einem keydown mit
// DRUCKBARER Taste (oder einer Meta-/Strg-Kombination) bei geschlossener
// Bildschirmtastatur — und wird ZURUECKGESETZT, sobald die Bildschirmtastatur
// erscheint (Ueberlappung > 80 px). So bricht ⏎ wieder um, wenn das Magic
// Keyboard abgezogen wird. ⌘↩/Strg+↩ sendet unabhaengig davon IMMER (null
// Fehlerfaelle) — das entscheidet der Aufrufer, nicht dieser Hook.
//
// Bewusst NICHT als Signal gewertet: Enter und Backspace — genau diese Tasten
// feuern Bildschirmtastaturen (GBoard & Co.) als echte keydown-Events. Ein
// Soft-Enter darf niemals der „Beweis" sein, der kuenftige Soft-Enter absendet.
// ============================================================================

// Ueberlappungs-Schwelle aus dem Plan (docs/30 §9.9: „> 80 px").
const OVERLAP_PX = 80;

// Groesste bisher gesehene Layout-Viewport-Hoehe. Noetig fuer Android-WebViews
// im adjustResize-Modus: dort schrumpft window.innerHeight MIT der Tastatur
// (visualViewport-Differenz bleibt ~0), die Oeffnung ist also nur am Verlust
// gegenueber dem Maximum erkennbar. Bei Orientierungswechsel neu geeicht.
let maxInnerHeight = 0;

function trackMaxInnerHeight(): void {
  if (typeof window === 'undefined') return;
  if (window.innerHeight > maxInnerHeight) maxInnerHeight = window.innerHeight;
}

if (typeof window !== 'undefined') {
  trackMaxInnerHeight();
  window.addEventListener('resize', trackMaxInnerHeight);
  window.addEventListener('orientationchange', () => {
    // Nach Rotation ist die alte Maxhoehe bedeutungslos — neu eichen, sobald
    // das Layout steht (zwei rAF, damit innerHeight schon rotiert ist).
    requestAnimationFrame(() => requestAnimationFrame(() => {
      maxInnerHeight = window.innerHeight;
    }));
  });
}

// Ist die Bildschirmtastatur gerade (oder soeben) offen? Drei Quellen:
// 1. `--keyboard-inset` — schreibt die Inset-Bruecke (src/layout/insets.ts, in
//    E1 aus useKeyboardInset.ts absorbiert) auf <html>. Das ist der
//    load-bearing Name; `--kb-inset` existiert seit E1 als reiner Alias
//    (layout.css --inset-bottom) und traegt denselben Wert — hier wird bewusst
//    der primaere Name gelesen.
// 2. visualViewport-Ueberlappung > 80 px (iOS/Chrome, Layout-Viewport stabil).
// 3. innerHeight-Verlust gegenueber dem Maximum (Android adjustResize, wo 1+2
//    blind sind, weil der ganze Layout-Viewport mitschrumpft).
export function softKeyboardOpen(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  try {
    const root = document.documentElement;
    if (root.hasAttribute('data-keyboard-open')) return true;
    const raw = getComputedStyle(root).getPropertyValue('--keyboard-inset').trim();
    const kbInset = raw ? parseFloat(raw) : 0;
    if (Number.isFinite(kbInset) && kbInset > OVERLAP_PX) return true;
  } catch {
    // getComputedStyle kann in seltenen Umgebungen werfen — dann kein Signal.
  }
  const vv = window.visualViewport;
  if (vv && typeof vv.height === 'number') {
    const overlap = window.innerHeight - vv.height - (vv.offsetTop || 0);
    if (overlap > OVERLAP_PX) return true;
  }
  if (maxInnerHeight > 0 && maxInnerHeight - window.innerHeight > OVERLAP_PX + 40) return true;
  return false;
}

// Tasten, die eine Bildschirmtastatur praktisch nie als echtes keydown sendet.
// Druckbare Zeichen kommen von Soft-Keyboards haeufig als `Unidentified` oder
// per Composition — das Restrisiko faengt softKeyboardOpen() ab.
//
// GENAU DAS ist das dritte Signal fuer die Bluetooth-Folio-Tastatur OHNE
// Trackpad (docs/30 §9.2/§9.9): an ihr scheitert jede Zeigerabfrage
// (any-pointer:fine ist coarse, kein pointerdown), aber sie sendet druckbare
// keydown-Events bei GESCHLOSSENER Bildschirmtastatur — genau die Bedingung,
// die useHardwareKeyboard() unten prueft. Enter/Backspace sind bewusst
// AUSGENOMMEN (Soft-Enter-Falle), das erledigt der Aufrufer bzw. useKeyDown.
function isHardwareSignal(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey) return true; // Meta-/Strg-Kombi (Plan)
  if (event.key.length === 1) return true; // druckbares Zeichen (Plan) — deckt Folio ohne Trackpad ab
  return ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape'].includes(event.key);
}

export function useHardwareKeyboard(): boolean {
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isHardwareSignal(event)) return;
      if (softKeyboardOpen()) return; // Bildschirmtastatur offen -> kein Hardware-Beweis
      setSeen(true);
    };

    // Reset-Pfad (Plan: „zurueckgesetzt, sobald visualViewport-Ueberlappung
    // > 80 px"): erscheint die Bildschirmtastatur, ist die Hardware-Tastatur
    // offensichtlich weg (mit angeschlossener Tastatur zeigt das System keine
    // Bildschirmtastatur). Beide Resize-Quellen abonnieren — visualViewport
    // fuer iOS, window fuer Android adjustResize.
    const onViewportChange = () => {
      if (softKeyboardOpen()) setSeen(false);
    };

    // Capture, damit der erste Anschlag global erkannt wird, egal wo der Fokus liegt.
    window.addEventListener('keydown', onKeyDown, true);
    window.visualViewport?.addEventListener('resize', onViewportChange);
    window.addEventListener('resize', onViewportChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.visualViewport?.removeEventListener('resize', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
    };
  }, []);

  return seen;
}

// ============================================================================
// useComposerInputMode (Paket N · docs/30 §9.2/§9.9/§12.2) — der benannte Seam,
// der die „Enter sendet?"-Entscheidung buendelt.
// ----------------------------------------------------------------------------
// Zwei-Achsen-Trennung (§9.2), die dieser Seam bewusst schuetzt:
//   • EINGABEART   (Hardware-Tastatur beobachtet? Zeiger fein?) — steuert, ob
//     nacktes ⏎ sendet und welche Affordanzen (Hinweistext etc.) sinnvoll sind.
//   • BILDSCHIRMBREITE (SizeClass) — ist hier bewusst NICHT enthalten: eine
//     spaetere Vereinfachung auf `width >= 1000` waere falsch (§12.2 Test 2).
//
// GESETZ (Wahrheitstabelle §9.9), das dieser Seam kapselt:
//   • ⌘↩ / Strg+↩ sendet IMMER — jeder Modus, null Fehlerfaelle.
//   • Nacktes ⏎ sendet NUR, wenn eine Hardware-Tastatur beobachtet wurde
//     (useHardwareKeyboard). Der Zeiger (usePointerClass) darf diese
//     Entscheidung NIEMALS allein kippen — sonst wuerde ein gekoppelter Apple
//     Pencil (any-pointer:fine, KEINE Taste) faelschlich Senden ausloesen.
//   • Shift+⏎ bleibt Umbruch; IME-Komposition sendet nie (Aufrufer-Guard).
//
// `pointer` wird NUR als KORROBORIERENDES Signal fuer UI-Affordanzen
// exportiert (z.B. „Enter zum Senden"-Hinweis nur an feinem Zeiger einblenden).
// Er fliesst NICHT in canEnterSend ein.
// ============================================================================

export interface ComposerInputMode {
  /** true, sobald eine echte Hardware-Tastatur beobachtet wurde (§9.9). */
  hardwareKeyboard: boolean;
  /**
   * Korroborierendes Signal fuer UI-Affordanzen (Zeigerklasse aus dem
   * Laufzeit-Detektor, NICHT (any-pointer:fine)). NIEMALS allein die
   * Sende-Entscheidung kippen — der Pencil-Fall (fine ohne Tastatur) muss
   * weiter umbrechen.
   */
  pointer: PointerClass;
  /**
   * true, wenn ein nacktes ⏎ (ohne Shift/Meta/Strg) SENDEN soll. Genau dann,
   * wenn eine Hardware-Tastatur beobachtet wurde — unabhaengig von Zeiger und
   * Bildschirmbreite. ⌘↩/Strg+↩ laeuft ueber `shouldSend`, nicht hierueber.
   */
  canEnterSend: boolean;
  /**
   * Zentrale Entscheidung fuer ein Enter-keydown im Composer. Kapselt die
   * komplette Wahrheitstabelle (§9.9) an EINER Stelle:
   *   • Shift+⏎            -> false (Umbruch)
   *   • ⌘↩ / Strg+↩        -> true  (sendet IMMER)
   *   • nacktes ⏎          -> canEnterSend (Hardware beobachtet?)
   * Erwartet, dass der Aufrufer IME-Komposition (isComposing/keyCode 229)
   * bereits abgefangen hat — Komposition darf nie senden.
   */
  shouldSend: (event: Pick<KeyboardEvent, 'shiftKey' | 'metaKey' | 'ctrlKey'>) => boolean;
}

export function useComposerInputMode(): ComposerInputMode {
  const hardwareKeyboard = useHardwareKeyboard();
  // Korroborierend fuer Affordanzen — bewusst NICHT Teil der Sende-Logik.
  const pointer = usePointerClass();

  const canEnterSend = hardwareKeyboard;

  const shouldSend: ComposerInputMode['shouldSend'] = (event) => {
    // Shift+⏎ = Umbruch, hat Vorrang.
    if (event.shiftKey && !(event.metaKey || event.ctrlKey)) return false;
    // ⌘↩ / Strg+↩ sendet IMMER — auch ohne beobachtete Tastatur, auch mit Shift.
    if (event.metaKey || event.ctrlKey) return true;
    // Nacktes ⏎: nur bei beobachteter Hardware-Tastatur.
    return canEnterSend;
  };

  return { hardwareKeyboard, pointer, canEnterSend, shouldSend };
}
